import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useChatPromptQueueStore } from '@/stores/chat-prompt-queue-store';

/** What the legacy composer and the queue strip need from the queue. */
export interface ChatPromptQueueControls {
  /** This chat's bucket in the queue store. */
  queueKey: string;
  enqueue: (text: string) => void;
  /**
   * Lift a hold and, when no turn is in flight, dispatch the next prompt
   * immediately. Also how a reloaded queue is set going again.
   */
  resume: () => void;
}

interface UseChatPromptQueueOptions {
  /**
   * The bucket prompts queue under. The caller keys it per workflow AND
   * per chat mode (node vs general) so a mode flip can never dispatch one
   * mode's prompts into the other's session.
   */
  queueKey: string;
  /** A turn is in flight for the current mode (what used to disable sends). */
  isBusy: boolean;
  /**
   * The current mode's session could take a message right now (connected,
   * idle, not denied). Dispatch is skipped -- prompts stay queued -- while
   * this is false.
   */
  canSend: boolean;
  /**
   * A clarification card owns the composer: its submissions are answers,
   * so a queued prompt must not be fired into (or past) the question.
   */
  hasPendingClarification: boolean;
  /**
   * Sends over the websocket session. Returns false when the send was
   * refused outright (socket gone), meaning nothing went out.
   */
  sendMessage: (text: string) => boolean;
}

/**
 * FIFO auto-dispatch for prompts queued while a legacy canvas-chat turn is
 * in flight. The websocket sibling of agent-chat's usePromptQueueDispatch,
 * sharing its store and invariants:
 *
 * Keys off the same signal that re-enables the composer -- the isBusy
 * true->false transition (node chat: awaiting-input restored; general chat:
 * awaiting_user_input frame) -- so "turn truly complete" can never disagree
 * with what the composer shows.
 *
 * The queue HOLDS instead of dispatching when the turn ended badly or
 * interactively:
 *   - the session can no longer take a message (socket closed/denied --
 *     the legacy stack's equivalent of a turn error),
 *   - a clarification card is up (a queued follow-up must not steamroll
 *     a question the workflow is waiting on).
 * A hold is lifted by the strip's resume button, or by any manual send --
 * a fresh turn starting is the user telling us the path works again.
 *
 * The turn's bucket is pinned when it starts (sendingKeyRef): a mode flip
 * mid-turn (node session ends -> general) changes the current key in the
 * same render that flips isBusy off, and the NEW mode's queue must not
 * fire off the OLD mode's ending. No draft-handoff machinery is needed
 * here -- the workflow id exists before the chat ever opens.
 */
export function useChatPromptQueue({
  queueKey,
  isBusy,
  canSend,
  hasPendingClarification,
  sendMessage,
}: UseChatPromptQueueOptions): ChatPromptQueueControls {
  const queueKeyRef = useRef(queueKey);
  queueKeyRef.current = queueKey;
  const canSendRef = useRef(canSend);
  canSendRef.current = canSend;
  const clarificationRef = useRef(hasPendingClarification);
  clarificationRef.current = hasPendingClarification;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  // Seeded false, NOT with the prop: a hook that mounts while a turn is
  // already in flight must still see that turn "start" on its first effect
  // run, or the completion transition would go unrecorded and undispatched.
  const isBusyRef = useRef(false);

  // The bucket the in-flight turn was sent against (see module doc).
  const sendingKeyRef = useRef<string | null>(null);

  const dispatchNext = useCallback((key: string) => {
    // Checking before the dequeue keeps the prompt queued instead of
    // losing it to a send that could never go out (or firing a chat
    // message into a pending clarification).
    if (!canSendRef.current || clarificationRef.current) return;
    const next = useChatPromptQueueStore.getState().dequeue(key);
    if (!next) return;
    const acted = sendMessageRef.current(next.text);
    // Refused outright (socket gone between the check and the send):
    // nothing went out, so the prompt goes back to the head for the next
    // completion to pick up.
    if (!acted) {
      useChatPromptQueueStore.getState().requeueFront(key, next);
    }
  }, []);

  useEffect(() => {
    const wasBusy = isBusyRef.current;
    isBusyRef.current = isBusy;
    const store = useChatPromptQueueStore.getState();
    if (isBusy && !wasBusy) {
      sendingKeyRef.current = queueKey;
      // Any manually started turn lifts a hold: the user has shown the
      // path works (or answered the question) themselves.
      store.setHeld(queueKey, false);
      return;
    }
    if (!isBusy && wasBusy) {
      const endedKey = sendingKeyRef.current;
      sendingKeyRef.current = null;
      if (endedKey !== queueKey) return;
      if (!canSend || hasPendingClarification) {
        store.setHeld(queueKey, true);
        return;
      }
      if (store.heldKeys[queueKey]) return;
      dispatchNext(queueKey);
    }
  }, [isBusy, queueKey, canSend, hasPendingClarification, dispatchNext]);

  const enqueue = useCallback(
    (text: string) => {
      useChatPromptQueueStore.getState().enqueue(queueKeyRef.current, text);
    },
    // queueKeyRef tracks the key; the callback can stay stable.
    [],
  );

  const resume = useCallback(() => {
    useChatPromptQueueStore.getState().setHeld(queueKeyRef.current, false);
    if (!isBusyRef.current) dispatchNext(queueKeyRef.current);
  }, [dispatchNext]);

  return useMemo(
    () => ({ queueKey, enqueue, resume }),
    [queueKey, enqueue, resume],
  );
}
